/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMutation } from "@apollo/client";
import { showNotification, updateNotification } from "@mantine/notifications";
import { FC, Fragment, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-location";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { authenticate } from "../../features/users";
import {
    GenerateOtp,
    GenerateOtpVariables,
} from "../../graphql/mutations/__generated__/GenerateOtp";
import {
    Login,
    LoginVariables,
    Login_login,
} from "../../graphql/mutations/__generated__/Login";
import {
    ValidateOtp,
    ValidateOtpVariables,
} from "../../graphql/mutations/__generated__/ValidateOtp";
import { getOTP, userLogin, validateOTP } from "../../graphql/mutations/auth";
import { usePage } from "../../hooks/usePage";

interface LoginScreenProps {}

const LoginScreen: FC<LoginScreenProps> = () => {
    const { t } = useTranslation();
    usePage(`Kava | ${t("login_page.title")}`);
    const navigate = useNavigate();
    const [step, setstep] = useState(0);
    const [otp, setOtp] = useState("");
    const dispatch = useAppDispatch();
    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm<LoginVariables & { otp: string }>();

    const [login, { loading }] = useMutation<Login, LoginVariables>(userLogin);
    const [generateOtp, { loading: generating }] = useMutation<
        GenerateOtp,
        GenerateOtpVariables
    >(getOTP);
    const [validate, { loading: validating }] = useMutation<
        ValidateOtp,
        ValidateOtpVariables
    >(validateOTP);

    const onSubmit: SubmitHandler<LoginVariables & { otp: string }> = (
        data,
    ) => {
        if (step === 0) {
            showNotification({
                id: "login",
                loading: true,
                title: t("login_page.notifications.getting_otp_title"),
                message: t("login_page.notifications.getting_otp_message"),
                autoClose: false,
                disallowClose: true,
            });
            generateOtp({
                variables: {
                    args: {
                        identifier: data?.input?.username || "",
                    },
                },
            })
                .then((res) => {
                    if (res.data?.generateOtp?.success) {
                        const message = res.data?.generateOtp?.message || "";

                        if (message.includes("OTP disabled by user")) {
                            setstep(2);
                            updateNotification({
                                id: "login",
                                title: t(
                                    "login_page.notifications.success_title",
                                ),
                                message: t(
                                    "login_page.notifications.otp_disabled_message",
                                ),
                                color: "blue",
                            });
                        } else {
                            setstep(1);
                            setOtp(res.data?.generateOtp?.otp || "");
                            updateNotification({
                                id: "login",
                                title: t(
                                    "login_page.notifications.success_title",
                                ),
                                message: t(
                                    "login_page.notifications.otp_sent_message",
                                ),
                                color: "blue",
                            });
                        }
                    } else {
                        updateNotification({
                            id: "login",
                            title: t("login_page.notifications.failed_title"),
                            message:
                                res.data?.generateOtp?.message ||
                                t(
                                    "login_page.notifications.otp_failed_message",
                                ),
                            color: "red",
                        });
                    }
                })
                .catch((error) => {
                    updateNotification({
                        id: "login",
                        title: t("login_page.notifications.error_title"),
                        message: t("login_page.notifications.email_not_found"),
                        color: "red",
                    });
                });

            return;
        }

        if (step === 1) {
            showNotification({
                id: "login",
                loading: true,
                title: t("login_page.notifications.validating_otp_title"),
                message: t("login_page.notifications.validating_otp_message"),
                autoClose: false,
                disallowClose: true,
            });
            validate({
                variables: {
                    args: {
                        identifier: data?.input?.username || "",
                        otp: data.otp,
                    },
                },
            })
                .then((res) => {
                    if (res?.data?.validateOtp?.success) {
                        updateNotification({
                            id: "login",
                            title: t("login_page.notifications.success_title"),
                            message:
                                res.data?.validateOtp?.message ||
                                t(
                                    "login_page.notifications.otp_correct_message",
                                ),
                            color: "blue",
                        });
                        setstep(2);
                    } else {
                        updateNotification({
                            id: "login",
                            title: t("login_page.notifications.error_title"),
                            message:
                                res.data?.validateOtp?.message ||
                                t(
                                    "login_page.notifications.otp_incorrect_message",
                                ),
                            color: "red",
                        });
                    }
                })
                .catch((err) => {
                    updateNotification({
                        id: "login",
                        title: t("login_page.notifications.error_title"),
                        message: t(
                            "login_page.notifications.otp_incorrect_message",
                        ),
                        color: "red",
                    });
                });
            return;
        }

        showNotification({
            id: "login",
            loading: true,
            title: t("login_page.notifications.logging_in_title"),
            message: t("login_page.notifications.logging_in_message"),
            autoClose: false,
            disallowClose: true,
        });
        login({ variables: data })
            .then((res) => {
                dispatch(authenticate(res.data?.login as Login_login));
                navigate({
                    to: "/app/dashboard",
                    replace: true,
                });
                updateNotification({
                    id: "login",
                    title: t("login_page.notifications.success_title"),
                    message: t(
                        "login_page.notifications.login_success_message",
                    ),
                    color: "blue",
                });
            })
            .catch(() => {
                updateNotification({
                    id: "login",
                    title: t("login_page.notifications.error_title"),
                    message: t(
                        "login_page.notifications.credentials_incorrect",
                    ),
                    color: "red",
                });
            });
    };

    return (
        <Fragment>
            <div className="flex flex-col items-center">
                <h2 className="mt-6 text-3xl font-medium text-gray-900">
                    {t("login_page.sign_in_title")}
                </h2>
            </div>
            <div className="mt-8 max-w-sm w-full">
                <div className="mt-6">
                    <form
                        className="space-y-6"
                        onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <div className="flex justify-between">
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium text-gray-700">
                                    {t("login_page.email_label")}
                                </label>
                            </div>
                            <div className="mt-1">
                                <input
                                    type="email"
                                    {...register("input.username", {
                                        required: t(
                                            "forms.validation.required",
                                        ),
                                    })}
                                    id="email"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                    placeholder={t(
                                        "login_page.email_placeholder",
                                    )}
                                    disabled={step > 0}
                                    aria-describedby="email-optional"
                                />
                                {errors?.input?.username && (
                                    <p className="text-red-400 text-sm mt-1">
                                        {errors?.input?.username?.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {step === 1 && (
                            <div>
                                <div className="flex justify-between">
                                    <label
                                        htmlFor="otp"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("login_page.otp_label")}
                                    </label>
                                </div>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        {...register("otp", {
                                            required: t(
                                                "forms.validation.required",
                                            ),
                                        })}
                                        id="otp"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                        placeholder={t(
                                            "login_page.otp_placeholder",
                                        )}
                                    />
                                    {errors?.otp && (
                                        <p className="text-red-400 text-sm mt-1">
                                            {errors?.otp?.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <div className="flex justify-between">
                                    <label
                                        htmlFor="password"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("login_page.password_label")}
                                    </label>
                                </div>
                                <div className="mt-1">
                                    <input
                                        type="password"
                                        {...register("input.password", {
                                            required: t(
                                                "forms.validation.required",
                                            ),
                                        })}
                                        id="password"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                        placeholder={t(
                                            "login_page.password_placeholder",
                                        )}
                                    />
                                    {errors?.input?.password && (
                                        <p className="text-red-400 text-sm mt-1">
                                            {errors?.input?.password?.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="text-sm">
                                <a
                                    onClick={() =>
                                        navigate({ to: "/forgot-password" })
                                    }
                                    className="font-medium text-green-600 cursor-pointer">
                                    {t("login_page.forgot_password")}
                                </a>
                            </div>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={loading || generating || validating}
                                className="w-full flex justify-center py-2 px-4 border bg-white rounded-md shadow-sm text-sm font-medium text-green-600 hover:text-white border-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 transition duration-200">
                                {step === 0
                                    ? t("login_page.authenticate_button")
                                    : step === 1
                                      ? t("login_page.verify_otp_button")
                                      : t("login_page.login_button")}
                                {(loading || generating || validating) && (
                                    <svg
                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default LoginScreen;
